import { useQuery } from 'libs/react';
import { useMemo } from 'react';
import { TaxesQuery } from 'graphql/Tax/queries/TaxesQuery';
import { getConfigurationLineTotal } from 'helpers/DealQuantity';
import { calculatePriceCurrency, getMargin, getPriceFromItem } from 'helpers/Pricing';
/**
 * @function useDealTaxBreakdown
 * @since 1.0.0
 *
 * Computes the tax related totals for a deal (subtotal, taxable amount,
 * applied taxes, total, ...) along with a per-line breakdown (items,
 * packages and options) indicating whether each line is taxable and which
 * additional taxes apply to it.
 *
 * This hook is intentionally free of any React context dependency so it can
 * be used both from components rendered within the deal configurator context
 * (e.g. `DealSummaryPanelStatement`) and from modal content, which is
 * rendered outside of that context tree (`useDealConfiguratorContext` would
 * return its default value there).
 *
 * @hidden
 */
export function useDealTaxBreakdown(deal, discountRep = 0) {
    var _a, _b, _c;
    const [taxesRates, ready] = useQuery(TaxesQuery, {
        variables: {
            input: {
                query: [
                    {
                        field: 'account',
                        operator: 'eq',
                        value: (_c = (_b = (_a = deal === null || deal === void 0 ? void 0 : deal.user) === null || _a === void 0 ? void 0 : _a.account) === null || _b === void 0 ? void 0 : _b.id) !== null && _c !== void 0 ? _c : '1'
                    }
                ]
            }
        }
    });
    const { subTotal, taxableAmount, costTotal, otherFeesTotal, lines } = useMemo(() => {
        var _a, _b, _c;
        let taxableAmount = 0;
        let costTotal = 0;
        const lines = [];
        const subTotal = (_a = deal === null || deal === void 0 ? void 0 : deal.items.reduce((acc, currItem) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w;
            if (currItem.proposed) {
                return acc;
            }
            let totalValue = currItem.price * currItem.quantity;
            const catalogUnitPrice = calculatePriceCurrency((_d = (_b = (_a = currItem.variant) === null || _a === void 0 ? void 0 : _a.price) !== null && _b !== void 0 ? _b : (_c = currItem.item) === null || _c === void 0 ? void 0 : _c.price) !== null && _d !== void 0 ? _d : 0, deal === null || deal === void 0 ? void 0 : deal.currency);
            costTotal +=
                (catalogUnitPrice - (discountRep * catalogUnitPrice) / 100) *
                    currItem.quantity;
            const itemTaxable = !!currItem.item.taxable;
            if (itemTaxable) {
                taxableAmount += totalValue;
            }
            lines.push({
                id: currItem.id,
                name: currItem.item.title,
                type: 'item',
                taxable: itemTaxable,
                lineAmount: currItem.price * currItem.quantity,
                additionalTaxes: itemTaxable
                    ? ((_e = currItem.item.taxes) !== null && _e !== void 0 ? _e : []).map((tax) => ({
                        id: tax.id,
                        title: tax.title,
                        rate: tax.rate
                    }))
                    : []
            });
            if (currItem.packages) {
                for (const configuration of currItem.packages) {
                    if (configuration.proposed) {
                        continue;
                    }
                    const lineTotal = getConfigurationLineTotal(configuration.price, configuration.quantity, (_f = configuration.package) === null || _f === void 0 ? void 0 : _f.quantityCalculation, deal);
                    totalValue += lineTotal;
                    const packageCatalogUnit = calculatePriceCurrency((_h = (_g = configuration.package) === null || _g === void 0 ? void 0 : _g.price) !== null && _h !== void 0 ? _h : 0, deal === null || deal === void 0 ? void 0 : deal.currency);
                    costTotal += getConfigurationLineTotal(packageCatalogUnit - (discountRep * packageCatalogUnit) / 100, configuration.quantity, (_j = configuration.package) === null || _j === void 0 ? void 0 : _j.quantityCalculation, deal);
                    const packageTaxable = !!((_k = configuration.package) === null || _k === void 0 ? void 0 : _k.taxable);
                    if (packageTaxable) {
                        taxableAmount += lineTotal;
                    }
                    lines.push({
                        id: configuration.id,
                        name: (_m = (_l = configuration.package) === null || _l === void 0 ? void 0 : _l.title) !== null && _m !== void 0 ? _m : '',
                        type: 'package',
                        taxable: packageTaxable,
                        lineAmount: lineTotal,
                        // `SaleItemPackage` has no per-item `taxes` override, only
                        // the regional/default taxes apply.
                        additionalTaxes: []
                    });
                }
            }
            if (currItem.options) {
                for (const configuration of currItem.options) {
                    if (configuration.proposed) {
                        continue;
                    }
                    const lineTotal = getConfigurationLineTotal(configuration.price, configuration.quantity, (_o = configuration.item) === null || _o === void 0 ? void 0 : _o.quantityCalculation, deal);
                    totalValue += lineTotal;
                    const optionCatalogUnit = getPriceFromItem(currItem.item, (_p = currItem.variant) !== null && _p !== void 0 ? _p : null, (_q = configuration.item) !== null && _q !== void 0 ? _q : null, 'option', deal === null || deal === void 0 ? void 0 : deal.currency);
                    costTotal += getConfigurationLineTotal(optionCatalogUnit - (discountRep * optionCatalogUnit) / 100, configuration.quantity, (_r = configuration.item) === null || _r === void 0 ? void 0 : _r.quantityCalculation, deal);
                    const optionTaxable = !!((_s = configuration.item) === null || _s === void 0 ? void 0 : _s.taxable);
                    if (optionTaxable) {
                        taxableAmount += lineTotal;
                    }
                    lines.push({
                        id: configuration.id,
                        name: (_u = (_t = configuration.item) === null || _t === void 0 ? void 0 : _t.title) !== null && _u !== void 0 ? _u : '',
                        type: 'option',
                        taxable: optionTaxable,
                        lineAmount: lineTotal,
                        additionalTaxes: optionTaxable
                            ? ((_w = (_v = configuration.item) === null || _v === void 0 ? void 0 : _v.taxes) !== null && _w !== void 0 ? _w : []).map((tax) => ({
                                id: tax.id,
                                title: tax.title,
                                rate: tax.rate
                            }))
                            : []
                    });
                }
            }
            return acc + totalValue;
        }, 0)) !== null && _a !== void 0 ? _a : 0;
        return {
            subTotal,
            taxableAmount,
            costTotal,
            otherFeesTotal: (_c = (_b = deal === null || deal === void 0 ? void 0 : deal.otherFees) === null || _b === void 0 ? void 0 : _b.reduce((acc, curr) => acc + curr.price, 0)) !== null && _c !== void 0 ? _c : 0,
            lines
        };
    }, [deal, discountRep]);
    const { total, taxes, appliedTaxes, discountValue, additionalTaxesTotal } = useMemo(() => {
        var _a, _b, _c;
        let taxes = 0;
        let discountValue = 0;
        let total = 0;
        total += subTotal;
        const d = deal && deal.discount ? (isNaN(deal.discount) ? 0 : deal.discount) : 0;
        if ((deal === null || deal === void 0 ? void 0 : deal.discountType) === 'AMOUNT') {
            discountValue = d !== null && d !== void 0 ? d : 0;
            total -= discountValue;
        }
        else {
            discountValue = (subTotal * (d !== null && d !== void 0 ? d : 0)) / 100;
            total -= discountValue;
        }
        if (otherFeesTotal) {
            total += otherFeesTotal;
        }
        const additionalTaxesTotal = (_a = deal === null || deal === void 0 ? void 0 : deal.items.reduce((acc, currItem) => {
            var _a, _b, _c, _d, _e, _f;
            if (currItem.proposed) {
                return acc;
            }
            let additional = 0;
            if (currItem.item.taxable) {
                additional +=
                    currItem.price *
                        currItem.quantity *
                        (((_b = (_a = currItem.item.taxes) === null || _a === void 0 ? void 0 : _a.reduce((acc, curr) => acc + curr.rate, 0)) !== null && _b !== void 0 ? _b : 0) /
                            100);
            }
            // `SaleItemPackage` has no per-item `taxes` override, so packages
            // never contribute to the "additional taxes" total (only the
            // regional/default taxes, already accounted for via
            // `taxableAmount`).
            if (currItem.options) {
                for (const configuration of currItem.options) {
                    if (!configuration.proposed && ((_c = configuration.item) === null || _c === void 0 ? void 0 : _c.taxable)) {
                        const lineTotal = getConfigurationLineTotal(configuration.price, configuration.quantity, (_d = configuration.item) === null || _d === void 0 ? void 0 : _d.quantityCalculation, deal);
                        additional +=
                            lineTotal *
                                (((_f = (_e = configuration.item.taxes) === null || _e === void 0 ? void 0 : _e.reduce((acc, curr) => acc + curr.rate, 0)) !== null && _f !== void 0 ? _f : 0) /
                                    100);
                    }
                }
            }
            return acc + additional;
        }, 0)) !== null && _a !== void 0 ? _a : 0;
        const appliedTaxes = [];
        if (taxableAmount > 0 || (otherFeesTotal || 0) > 0) {
            const shipProvinceCode = (_b = deal === null || deal === void 0 ? void 0 : deal.shipTo) === null || _b === void 0 ? void 0 : _b.province;
            const activeTaxes = (_c = taxesRates === null || taxesRates === void 0 ? void 0 : taxesRates.taxes.filter((t) => t.enabled && (t.code === shipProvinceCode || t.default))) !== null && _c !== void 0 ? _c : [];
            const taxBase = taxableAmount - discountValue + (otherFeesTotal !== null && otherFeesTotal !== void 0 ? otherFeesTotal : 0);
            for (const t of activeTaxes) {
                const amount = taxBase * (t.rate / 100);
                appliedTaxes.push({
                    id: t.id,
                    title: t.title,
                    rate: t.rate,
                    amount
                });
                taxes += amount;
            }
            taxes += additionalTaxesTotal;
            total += taxes;
        }
        return {
            total,
            taxes,
            appliedTaxes,
            discountValue,
            additionalTaxesTotal
        };
    }, [deal, subTotal, otherFeesTotal, taxableAmount, taxesRates]);
    const marginPercent = useMemo(() => {
        const revenueHT = subTotal - discountValue + (otherFeesTotal !== null && otherFeesTotal !== void 0 ? otherFeesTotal : 0);
        const dealerCost = costTotal + (otherFeesTotal !== null && otherFeesTotal !== void 0 ? otherFeesTotal : 0);
        return getMargin(dealerCost, revenueHT);
    }, [subTotal, discountValue, costTotal, otherFeesTotal]);
    const adjustedSubtotal = useMemo(() => {
        const baseTotal = subTotal + (otherFeesTotal !== null && otherFeesTotal !== void 0 ? otherFeesTotal : 0);
        const d = deal && deal.discount ? (isNaN(deal.discount) ? 0 : deal.discount) : 0;
        if ((deal === null || deal === void 0 ? void 0 : deal.discountType) === 'AMOUNT') {
            return baseTotal - (d !== null && d !== void 0 ? d : 0);
        }
        return baseTotal - baseTotal * ((d !== null && d !== void 0 ? d : 0) / 100);
    }, [subTotal, otherFeesTotal, deal === null || deal === void 0 ? void 0 : deal.discount, deal === null || deal === void 0 ? void 0 : deal.discountType]);
    return {
        additionalTaxesTotal,
        adjustedSubtotal,
        appliedTaxes,
        costTotal,
        discountValue,
        lines,
        marginPercent,
        otherFeesTotal,
        ready,
        subTotal,
        taxableAmount,
        taxes,
        total
    };
}
